// src/apiClient.ts
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BadRequestError, BaseError, ConflictError, ForbiddenError, NotFoundError, TimeoutError, UnauthorizedError, ValidationError, } from './type';
export const DEFAULT_TIMEOUT = 30 * 1000;
export class PotentialApiClient {
    constructor(config) {
        /**
         * BadRequestのエラーを処理する
         *
         * @param data
         * @returns void
         */
        this.handleBadRequest = (data) => {
            if (typeof data.error === 'object' && data.error !== null) {
                if ('validation_errors' in data.error) {
                    throw new ValidationError('Bad Request', data.error.validation_errors);
                }
            }
            else {
                throw new BadRequestError(data.error);
            }
        };
        const { apiUrlMappings = [], apiKey } = config;
        this.apiUrlMappings = apiUrlMappings;
        this.apiKey = apiKey;
    }
    /**
     * エンドポイントに対応するベースURLを取得する
     *
     * @param endpoint - APIエンドポイントのパス
     * @returns 対応するベースURL、存在しない場合はデフォルトのベースURLを返す
     */
    getBaseUrl(endpoint) {
        for (const [pathPattern, baseUrl] of this.apiUrlMappings) {
            const pathRegex = new RegExp(`^${pathPattern.replace(/:\w+/g, '\\w+').replace('/**', '(/|$).*')}$`);
            if (pathRegex.test(endpoint)) {
                return baseUrl;
            }
        }
        return '';
    }
    /**
     * GETリクエストのオプションを返す
     *
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    getRequestOptions(headers = {}) {
        return {
            method: 'GET',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
        };
    }
    /**
     * POSTリクエストのオプションを返す
     *
     * @param body - POSTリクエストのボディデータ
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    postRequestOptions(body, headers = {}) {
        return {
            method: 'POST',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
            body: JSON.stringify(body),
        };
    }
    /**
     * FormData 用の POST リクエストのオプションを返す
     *
     * @param body - POSTリクエストの FormData オブジェクト
     * @param headers - 任意のヘッダーオプション（Content-Type は自動設定されるため指定しない）
     * @returns リクエストオプション
     */
    postRequestOptionsFormData(body, headers = {}) {
        return {
            method: 'POST',
            headers: Object.assign(Object.assign({}, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
            body,
        };
    }
    /**
     * FormのPOSTリクエストオプションを返す
     *
     * @param body - POSTリクエストのボディデータ
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    postFormRequestOptions(body, headers = {}) {
        return {
            method: 'POST',
            headers: Object.assign(Object.assign({}, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
            body: JSON.stringify(body),
        };
    }
    /**
     * PUTリクエストのオプションを返す
     *
     * @param body - PUTリクエストのボディデータ
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    putRequestOptions(body, headers = {}) {
        return {
            method: 'PUT',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
            body: JSON.stringify(body),
        };
    }
    /**
     * PATCHリクエストのオプションを返す
     *
     * @param body - PATCHリクエストのボディデータ
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    patchRequestOptions(body, headers = {}) {
        return {
            method: 'PATCH',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
            body: JSON.stringify(body),
        };
    }
    /**
     * DELETEリクエストのオプションを返す
     *
     * @param headers - 任意のヘッダーオプション
     * @returns リクエストオプション
     */
    deleteRequestOptions(headers = {}) {
        return {
            method: 'DELETE',
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (this.apiKey ? { 'X-Api-Key': this.apiKey } : {})), headers),
        };
    }
    /**
     * ステータスエラーを処理する
     *
     * @param response - フェッチのレスポンス
     * @returns void
     */
    handleStatusError(response) {
        return __awaiter(this, void 0, void 0, function* () {
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                throw new Error('Failed to parse JSON response');
            }
            switch (response.status) {
                case 400:
                    this.handleBadRequest(data);
                    break;
                case 401:
                    throw new UnauthorizedError(data.error, data.errorCode);
                case 403:
                    throw new ForbiddenError(data.error, data.errorCode);
                case 404:
                    throw new NotFoundError(data.error, data.errorCode);
                case 409:
                    throw new ConflictError(data.error, data.errorCode);
                case 500:
                    throw new BaseError(data.error, data.errorCode);
                default:
                    throw new Error('Network response was not ok');
            }
        });
    }
    /**
     * APIからデータを取得する
     *
     * @param endpoint - APIエンドポイントのパス
     * @param options - リクエストオプション
     * @param queryString - 任意のクエリパラメータ
     * @returns APIから取得したデータ
     */
    fetchFromAPI(_a) {
        return __awaiter(this, arguments, void 0, function* ({ endpoint, queryString = '', options, }) {
            if (options.credentials == null) {
                options.credentials = 'include';
            }
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                controller.abort();
            }, DEFAULT_TIMEOUT);
            options.signal = controller.signal;
            const baseUrl = this.getBaseUrl(endpoint);
            if (baseUrl === '') {
                throw new Error('Base URL not found');
            }
            try {
                const response = yield fetch(`${baseUrl}${endpoint}${queryString}`, options);
                clearTimeout(timeoutId);
                if (!response.ok) {
                    yield this.handleStatusError(response);
                }
                const data = yield response.json();
                return data;
            }
            catch (err) {
                clearTimeout(timeoutId);
                if (err instanceof Error && err.name === 'AbortError') {
                    throw new TimeoutError(`Request timed out after ${DEFAULT_TIMEOUT}ms`);
                }
                throw err;
            }
        });
    }
}
//# sourceMappingURL=apiClient.js.map